/**
 * Runtime Service URLs
 *
 * Fetches service URLs from /.netlify/functions/config at runtime.
 * No more VITE_ build-time env vars for service URLs.
 * Adding a new service URL only requires a Netlify env var — no rebuild.
 *
 * Falls back to import.meta.env for dev/local use.
 *
 * IMPORTANT — sync named exports (SOCIAL_SERVICE_URL etc.) are FROZEN at
 * module load time. If the runtime config has not loaded yet, they fall back
 * to build-time VITE_* env vars, which can be stale relative to the Netlify
 * env var of the same name. ALWAYS prefer `getUrlAsync(key)` for new code,
 * especially for fetches fired in useEffect / event handlers that may race
 * the config fetch. The sync exports are kept for backward-compat only.
 */

import { logError, logWarn } from './betterstack-logger';

let _config = null;
let _configPromise = null;
let _configError = null;

async function fetchConfig() {
  try {
    const res = await fetch('/.netlify/functions/config');
    if (!res.ok) throw new Error(`Config fetch failed: ${res.status}`);
    _config = await res.json();
    return _config;
  } catch (err) {
    console.error('❌ Failed to fetch runtime config:', err.message);
    logError('config-fetch-failed', { error: err.message });
    _configError = err;
    // Fall back to build-time env vars
    _config = {
      SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL || '',
      SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY || '',
      ASIN_CORRELATION_API_URL: import.meta.env.VITE_ASIN_CORRELATION_API_URL || '',
      CATALOG_API_URL: import.meta.env.VITE_CATALOG_API_URL || '',
      MEDIA_SERVICE_URL: import.meta.env.VITE_MEDIA_SERVICE_URL || '',
      AUTH_SERVICE_URL: import.meta.env.VITE_AUTH_SERVICE_URL || '',
      SOCIAL_SERVICE_URL: import.meta.env.VITE_SOCIAL_SERVICE_URL || '',
      A2A_API_URL: import.meta.env.VITE_A2A_API_URL || '',
      EBAY_SERVICE_URL: import.meta.env.VITE_EBAY_SERVICE_URL || '',
      ORDER_TRACKING_API_URL: import.meta.env.VITE_ORDER_TRACKING_API_URL || '',
      EMAIL_INGESTION_URL: import.meta.env.VITE_EMAIL_INGESTION_URL || '',
      LAUNCH_DASHBOARD_API_URL: import.meta.env.VITE_LAUNCH_DASHBOARD_API_URL || '',
      BILLING_SERVICE_URL: import.meta.env.VITE_BILLING_SERVICE_URL || '',
      AUTOMATION_SERVICE_URL: import.meta.env.VITE_AUTOMATION_SERVICE_URL || '',
      A2E_API_URL: import.meta.env.VITE_A2E_API_URL || '',
      SOCIAL_OAUTH_REDIRECT_BASE:
        import.meta.env.VITE_SOCIAL_OAUTH_REDIRECT_BASE ||
        import.meta.env.VITE_SOCIAL_SERVICE_URL ||
        '',
    };
    return _config;
  }
}

// Initialize config fetch immediately
export function initConfig() {
  if (!_configPromise) {
    _configPromise = fetchConfig();
  }
  return _configPromise;
}

// Start fetching immediately on module load
initConfig();

// Track keys we've already warned about so we don't spam the console/logs.
const _warnedKeys = new Set();

// Synchronous getter — returns cached value or build-time fallback.
// Emits a one-time warning (per key) when the sync path is hit before
// runtime config has loaded. This is the common footgun that causes features
// to silently break when the build-time VITE_* value is stale relative to
// the runtime Netlify env var of the same name. Fix by switching the caller
// to `getUrlAsync(key)`.
function getUrl(key) {
  if (_config) return _config[key] || '';
  // Before config loads, fall back to build-time env
  const viteKey = `VITE_${key}`;
  const fallback = import.meta.env[viteKey] || '';
  // Only warn when the build-time fallback is EMPTY — that's the actually
  // broken case. When the fallback is populated, the sync path works fine
  // (though it risks being stale vs. runtime config — still prefer async).
  if (!fallback && !_warnedKeys.has(key)) {
    _warnedKeys.add(key);
    const msg = `service-urls: sync read of ${key} before runtime config loaded and VITE_${key} is empty. Caller will get ''. Switch to getUrlAsync().`;
    console.warn(msg);
    // OPS-58: this is a steady-state, recoverable fallback (sync read raced the
    // runtime /config fetch). Log at warn, not error, so it stops generating ops
    // incidents. Root cause (the sync-export race) is tracked under OPS-167.
    try { logWarn('service-urls-sync-empty-fallback', { key }); } catch (_e) { /* ignore */ }
  }
  return fallback;
}

// Async getter — waits for config to load
export async function getUrlAsync(key) {
  await _configPromise;
  return _config?.[key] || '';
}

// Export getters (synchronous — work after config loads, fallback to VITE_ before)

// Named exports — resolve from runtime config or fall back to build-time env
export const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || '';
export const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
export const ASIN_CORRELATION_API_URL = getUrl('ASIN_CORRELATION_API_URL');
export const CATALOG_API_URL = getUrl('CATALOG_API_URL');
export const MEDIA_SERVICE_URL = getUrl('MEDIA_SERVICE_URL');
export const AUTH_SERVICE_URL = getUrl('AUTH_SERVICE_URL');
export const SOCIAL_SERVICE_URL = getUrl('SOCIAL_SERVICE_URL');
export const A2A_API_URL = getUrl('A2A_API_URL');
export const EBAY_SERVICE_URL = getUrl('EBAY_SERVICE_URL');
export const ORDER_TRACKING_API_URL = getUrl('ORDER_TRACKING_API_URL');
export const EMAIL_INGESTION_URL = getUrl('EMAIL_INGESTION_URL');
export const LAUNCH_DASHBOARD_API_URL = getUrl('LAUNCH_DASHBOARD_API_URL');
export const BILLING_SERVICE_URL = getUrl('BILLING_SERVICE_URL');
export const AUTOMATION_SERVICE_URL = getUrl('AUTOMATION_SERVICE_URL');
// NOTE: A2E_API_URL and SOCIAL_OAUTH_REDIRECT_BASE intentionally have NO sync
// named export. Both keys are absent from frontend/.env.* (sparse build-time
// fallback), so a module-load getUrl() for them hit the empty-fallback branch
// and emitted `service-urls-sync-empty-fallback` on every page load (OPS-30).
// Neither has a synchronous consumer — the only live reader is
// AmazonToEbayScanner.jsx via getUrlAsync('A2E_API_URL'). Use getUrlAsync(key).
export const NETLIFY_FUNCTIONS_URL = '/.netlify/functions';

// For dynamic access, use these functions
export function getServiceUrl(key) {
  return getUrl(key);
}

// Wait for config before first use
export async function waitForConfig() {
  await _configPromise;
  if (_configError && !_config) {
    throw new Error('Runtime config unavailable and no build-time fallback');
  }
  return _config;
}

export function getServiceStatus() {
  const c = _config || {};
  return {
    configLoaded: !!_config,
    configError: _configError?.message || null,
    supabase: !!c.SUPABASE_URL,
    asinCorrelation: !!c.ASIN_CORRELATION_API_URL,
    catalog: !!c.CATALOG_API_URL,
    media: !!c.MEDIA_SERVICE_URL,
    auth: !!c.AUTH_SERVICE_URL,
    social: !!c.SOCIAL_SERVICE_URL,
    a2a: !!c.A2A_API_URL,
    ebay: !!c.EBAY_SERVICE_URL,
    orderTracking: !!c.ORDER_TRACKING_API_URL,
    emailIngestion: !!c.EMAIL_INGESTION_URL,
    launchDashboard: !!c.LAUNCH_DASHBOARD_API_URL,
  };
}

export default {
  getServiceUrl,
  getUrlAsync,
  waitForConfig,
  initConfig,
  getServiceStatus,
  get SUPABASE_URL() { return getUrl('SUPABASE_URL'); },
  get SUPABASE_ANON_KEY() { return getUrl('SUPABASE_ANON_KEY'); },
  get ASIN_CORRELATION_API_URL() { return getUrl('ASIN_CORRELATION_API_URL'); },
  get CATALOG_API_URL() { return getUrl('CATALOG_API_URL'); },
  get MEDIA_SERVICE_URL() { return getUrl('MEDIA_SERVICE_URL'); },
  get AUTH_SERVICE_URL() { return getUrl('AUTH_SERVICE_URL'); },
  get SOCIAL_SERVICE_URL() { return getUrl('SOCIAL_SERVICE_URL'); },
  get A2A_API_URL() { return getUrl('A2A_API_URL'); },
  get EBAY_SERVICE_URL() { return getUrl('EBAY_SERVICE_URL'); },
  get ORDER_TRACKING_API_URL() { return getUrl('ORDER_TRACKING_API_URL'); },
  get EMAIL_INGESTION_URL() { return getUrl('EMAIL_INGESTION_URL'); },
  get LAUNCH_DASHBOARD_API_URL() { return getUrl('LAUNCH_DASHBOARD_API_URL'); },
  get BILLING_SERVICE_URL() { return getUrl('BILLING_SERVICE_URL'); },
  get AUTOMATION_SERVICE_URL() { return getUrl('AUTOMATION_SERVICE_URL'); },
  get A2E_API_URL() { return getUrl('A2E_API_URL'); },
  get NETLIFY_FUNCTIONS_URL() { return '/.netlify/functions'; },
};
